// ── SS2B-2b risk-response presentation model ──────────────────────────────────
//
// Three axes, three separate things on screen (the Claude Design north-star): response TYPE
// carries a hue + glyph (its own mechanism, borrows nothing from StatusBadge); workflow STATUS
// keeps the existing StatusBadge treatment; closure REASON is a phrase attached to the closed
// status, never a colour of its own. This module holds the type + stage + closure data only —
// the components ([[RrTypeChip]], [[RrStatusCell]]) render it.

import type { RiskResponseType } from '@/api/types/risk-acceptance.types';

// ── Response type (5) — hue + glyph mirror the Design template. The chip is a SEPARATE mechanism
// from StatusBadge (never a workflow-status colour). Accept amber · Avoid violet · Mitigate emerald
// · Share sky · Transfer slate.
export interface RrTypeSpec { name: string; glyph: string; hue: string; }
export const RR_TYPE_SPECS: Record<RiskResponseType, RrTypeSpec> = {
  accept:   { name: 'Accept',   glyph: 'A', hue: '#D4A843' },
  avoid:    { name: 'Avoid',    glyph: 'V', hue: '#A98FE0' },
  mitigate: { name: 'Mitigate', glyph: 'M', hue: '#5FB89A' },
  share:    { name: 'Share',    glyph: 'S', hue: '#6FBBEA' },
  transfer: { name: 'Transfer', glyph: 'T', hue: '#9FC0DE' },
};

// ── Stage = planned | implemented (honesty anchor 3) — a property of ONE record, NOT a workflow
// status and NOT a second row. All five types carry it (accepted divergence 1). Oracle:
// response_implemented_at presence (mirrors the ApprovedCell timestamp-presence oracle), corroborated
// by implemented_response_type. Planned = agreed and pending action — NEVER "overdue".
export type RrStage = 'planned' | 'implemented';
// Structural (string) shape — works for both RaEntry and the slim RenewalLineageEntry.
export interface RrStageSource {
  implemented_response_type: string | null;
  response_implemented_at: string | null;
}
export function deriveStage(entry: RrStageSource): RrStage {
  return entry.response_implemented_at != null || entry.implemented_response_type != null
    ? 'implemented'
    : 'planned';
}

// Structural shape for the type chip — planned type + the stage source (string-typed for reuse).
export interface RrTypeSource extends RrStageSource {
  planned_response_type: string;
}

// ── Closure reason → the phrase that extends the closed status (honesty anchors 2 + 6 + 7).
// NEVER a badge/colour of its own beyond the family tone. `remediated` is the ONLY green (favourable);
// `expired`/`revoked` are the ONLY warnings (unfavourable — cover lapsed); everything else, INCLUDING
// `replaced_by_renewal`, is NEUTRAL slate (a superseded response is housekeeping, not a lapse).
export type RrClosureFamily = 'favourable' | 'neutral' | 'unfavourable';
export interface RrClosureSpec {
  label: string;
  family: RrClosureFamily;
  variant: 'emerald' | 'muted' | 'rose';
}
// ── Active / Resolved register partition (SS2B-2b Phase 3, spec card 8). Resolved = the vendor is no
// longer carrying a live response (closed / denied / withdrawn). ⚠ HONESTY ANCHOR 1/5: `approved` is
// ACTIVE, so an implemented Mitigate (which STAYS approved) is ALWAYS Active — never Resolved. The
// partition is by workflow_status alone; the response type/stage never moves a row across the tab.
export const RR_ACTIVE_STATES = ['draft', 'pending_approval', 'returned', 'approved'] as const;
export const RR_RESOLVED_STATES = ['closed', 'denied', 'withdrawn'] as const;

export const RR_CLOSURE_SPECS: Record<string, RrClosureSpec> = {
  remediated:           { label: 'Remediated',                     family: 'favourable',   variant: 'emerald' },
  replaced_by_renewal:  { label: 'Superseded by renewal',          family: 'neutral',      variant: 'muted' },
  app_service_retired:  { label: 'Application or service retired', family: 'neutral',      variant: 'muted' },
  no_longer_applicable: { label: 'No longer applicable',           family: 'neutral',      variant: 'muted' },
  expired:              { label: 'Expired',                        family: 'unfavourable', variant: 'rose' },
  revoked:              { label: 'Revoked',                        family: 'unfavourable', variant: 'rose' },
  other:                { label: 'Other',                          family: 'neutral',      variant: 'muted' },
};
